import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {LocalTimeShort} from 'modules/Common/components/LocalDateTime';
import {Link} from 'react-router';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import FootballMatchEventIcon from 'modules/Football/components/svg/FootballMatchEventIcon';
import FootballMatchOutcomeDescription from 'modules/Football/components/FootballMatchOutcomeDescription';

import {globalCssExports} from 'utils/commonUtils';
import _ from "lodash";

const EVENT_TYPES = {
  "KICK_OFF":             timeFactory("Unknown Type", "Kick Off"),
  "END_OF_FIRST_HALF":    timeFactory("Half Time"   , "Half time"),
  "START_OF_SECOND_HALF": timeFactory("Unknown Type", "Start of second half"),
  "END_OF_SECOND_HALF":   timeFactory("Unknown Type", "End of second half"),
  "FULL_TIME":            timeFactory("Full Time"   , "Full Time", true, true),

  "START_OF_EXTRA_TIME":             timeFactory("Unknown Type", "Start of extra time"),
  "END_OF_EXTRA_TIME":               timeFactory("Unknown Type", "End of extra time"),
  "START_OF_PENALTY_SHOOTOUT":       timeFactory("Unknown Type", "Start of penalty shootout"),
  "END_OF_PENALTY_SHOOTOUT":         timeFactory("Unknown Type", "End of penalty shootout"),
  "END_OF_EXTRA_TIME_FIRST_HALF":    timeFactory("Unknown Type", "End of extra time first half"),
  "START_OF_EXTRA_TIME_SECOND_HALF": timeFactory("Unknown Type", "Start of extra time second half"),

  "GOAL":             goalFactory("Goal"   , "GOAL"),
  "OWN_GOAL":         goalFactory("Goal"   , "Own Goal"),
  "PENALTY":          goalFactory("Penalty", "Penalty Scored"),
  "SHOOTOUT_PENALTY": goalFactory("Penalty", "Shootout Penalty"),

  "YELLOW_CARD":        playerFactory("Yellow Card",        "Yellow Card"),
  "SECOND_YELLOW_CARD": playerFactory("Second Yellow Card", "2nd Yellow Card"),
  "RED_CARD":           playerFactory("Red Card",           "Red Card"),

  "SUBSTITUTION_ON":  playerFactory("Sub On" , "Substitution On"),
  "SUBSTITUTION_OFF": playerFactory("Sub Off", "Substitution Off")
};

////////////////////////////////
// Line factory helpers

function matchHref(lineData) {
  const {
    match_reference: {
      id: matchId = null,
    } = {}
  } = lineData;

  return matchId ?
    `/football/live/${matchId}/commentary` :
    "/football/live/vidiprinter";
}

function teamName(teamData) {
  const {
    team: {
      name: teamName = "",
    } = {}
  } = teamData;

  return teamName || "";
}

function teamScore(teamData) {
  const {
    score = [{
      score: null,
    }]
  } = teamData;

  return score[0].score || null;
}

function playerEvent(lineData) {
  const {
    player: {
      display_name = '',
    } = {},
    match_time = '',
  } = lineData;

  return display_name && match_time ?
    <span className="vidiline-player">({display_name} {match_time})</span> :
    '';
}

function isScore(lineData, teamCode) {
  const {
    team: {
      team_reference: {
        id: scoredTeam = "noscore"
      } = {}
    } = {},
    [`team_score_${teamCode}`]: {
      team: {
        team_reference: {
          id: thisTeam = "nothis"
        } = {}
      } = {}
    } = {}
  } = lineData;

  return scoredTeam == thisTeam;
}

function getAggregateScore(lineData) {
  const {
    team_score_a: {
      aggregate_score: teamA
    } = {},
    team_score_b: {
      aggregate_score: teamB
    } = {}
  } = lineData;

  if ((teamA !== undefined) && (teamB !== undefined)) {
    return `(Agg: ${teamA}-${teamB})`;
  }
}

function getPenaltyShootoutScore(lineData) {
  const {
    team_score_a: {
      penalty_shootout_score: teamA
    } = {},
    team_score_b: {
      penalty_shootout_score: teamB
    } = {}
  } = lineData;

  if ((teamA !== undefined) && (teamB !== undefined)) {
    return { home: teamA, away: teamB };
  }
}


////////////////////////////////
// Line factories

function timeFactory(icon, label, highlightMax = false, displayOutcome = false) {
  function highlightIfMax(lineData, teamCode) {
    if (highlightMax) {
      const min = Math.min(teamScore(lineData.team_score_a), teamScore(lineData.team_score_b));
      const score = teamScore(lineData[`team_score_${teamCode}`]);

      return (score > min)?"vidiline-highlight-team":"";
    }
    else {
      return "";
    }
  }

  return function time(lineData, key) {
    const aggregateScore = getAggregateScore(lineData);
    const penaltyShootoutScore = getPenaltyShootoutScore(lineData);

    return (<Line line={lineData} key={key}>
      <Time line={lineData}/>
      <Icon type={icon}/>
      <Message>
        <Link className="vidiline-link" to={matchHref(lineData)}>
          <span>{label} -</span>
          <span className={highlightIfMax(lineData, "a")}>{teamName(lineData.team_score_a)} {teamScore(lineData.team_score_a)}</span>
          <span className={highlightIfMax(lineData, "b")}>{teamName(lineData.team_score_b)} {teamScore(lineData.team_score_b)}</span>
          {aggregateScore && <span>{aggregateScore}</span>}
        </Link>
        {displayOutcome && 
          <FootballMatchOutcomeDescription matchOutcome={lineData.match_outcome} penaltyShootoutScore={penaltyShootoutScore} enableCompactText={false} style="vidiprinter-result"/>
        }
      </Message>
    </Line>);
  }
}

function goalFactory(icon, label) {
  function highlightIfScore(lineData, teamCode) {
    // If this is an own goal then we need to swap teams over for highlighting
    if (lineData.event_type.event_type == "OWN_GOAL") {
      teamCode = teamCode == "a" ? "b" : "a";
    }
    return isScore(lineData, teamCode)? "vidiline-highlight-score": "";
  }

  return function goal(lineData, key) {
    const aggregateScore = getAggregateScore(lineData);

    return (<Line line={lineData} key={key}>
      <Time line={lineData}/>
      <Icon type={icon}/>
      <Message>
        <Link className="vidiline-link" to={matchHref(lineData)}>
          <span className="vidiline-highlight-event">{label} -</span>
          <span className={highlightIfScore(lineData, "a")}>{teamName(lineData.team_score_a)} {teamScore(lineData.team_score_a)}</span>
          {isScore(lineData, "a") && playerEvent(lineData)}
          <span className={highlightIfScore(lineData, "b")}>{teamName(lineData.team_score_b)} {teamScore(lineData.team_score_b)}</span>
          {isScore(lineData, "b") && playerEvent(lineData)}
          {aggregateScore && <span>{aggregateScore}</span>}
        </Link>
      </Message>
    </Line>);
  }
}

function playerFactory(icon, label) {
  return function player(lineData, key) {
    return (<Line line={lineData} key={key}>
      <Time line={lineData}/>
      <Icon type={icon}/>
      <Message>
        <Link className="vidiline-link" to={matchHref(lineData)}>
          <span>{label} -</span>
          <span>{playerEvent(lineData)}</span>
          <span>{teamName(lineData)}</span>
        </Link>
      </Message>
    </Line>);
  }
}

function defaultLine(lineData, key) {
  return (<Line line={lineData} key={key}>
    <Time line={lineData}/>
    <Icon/>
    <Message>
      <Link className="vidiline-link" to={matchHref(lineData)}>
        <span>{lineData.vidiprinter_line}</span>
      </Link>
    </Message>
  </Line>);
}

function transform(lineData) {
  const key = lineData.match_event_reference.id;
  const {event_type: {event_type: eventType} = {}} = lineData;

  if (eventType && EVENT_TYPES[eventType]) {
    return EVENT_TYPES[eventType](lineData, key);
  }
  else {
    return defaultLine(lineData, key);
  }
}

////////////////////////////////
// Sub-components

function Line({children}) {
  return <div className="vidiline">
    {children}
  </div>
}
Line.propTypes = {
  children: PropTypes.node.isRequired
}

function Time({line: {date}}) {
  return <div className="vidiline-time"><LocalTimeShort iso={date}/></div>;
}
Time.propTypes = {
  line: PropTypes.shape({
    date: PropTypes.string.isRequired
  })
}

function Icon({type}) {
  return <div className="vidiline-icon">{type && <FootballMatchEventIcon type={type}/>}</div>;
}
Icon.propTypes = {
  type: PropTypes.string
}

function Message({children}) {
  return <div className="vidiline-message">{children}</div>;
}
Message.propTypes = {
  children: PropTypes.node.isRequired
}

////////////////////////////////
// Main component

export default class FootballVidiprinter extends Component {
  static propTypes = {
    data: PropTypes.object
  };

  render() {
    require('./FootballVidiprinter.scss');

    if (!_.get(this.props, 'data.lines')) {
      return (
        <div className="default-no-article">
          <p>The Vidiprinter is currently unavailable, please try again later.</p>
        </div>
      );
    }

    const { data: { lines = [] }  } = this.props;

    const {highlightNewDataDuration} = globalCssExports("animations");

    // transitionLeaveTimeout is set to 1ms due to how React's library checks for timeouts.
    // It does a check based on a truthy value and 0 would be false.
    return (
      <div className="vidiprinter-lines">
        <ReactCSSTransitionGroup transitionName="vidiprinter-newline" transitionEnterTimeout={highlightNewDataDuration} transitionLeaveTimeout={1}>
          {lines.map(transform)}
        </ReactCSSTransitionGroup>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/Football/components/FootballVidiprinter.js